import {useEffect, useState} from 'react';
import styles from './adminSettingsOverview.module.scss';
import {Dropdown, Form, Menu, Select, Table, Tag} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {GET_ALL_USER_ROLES} from 'graphql/queries/admin';
import dotsIcon from 'icons/dots.svg';
import {Button} from '@dabafinance/react-components';
import {useLocation, useNavigate} from 'react-router-dom';
import {GET_ALL_ADMIN_USERS} from 'graphql/queries/users';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ASSIGN_USER_ROLE, DELETE_ADMIN_USER} from 'graphql/mutations/admin';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import AdminDashboardMenuSettings from './AdminDashboardMenuSettings';

const AdminSettingOverview = () => {
  const [activeTab, setActiveTab] = useState('ADMINS');
  const navigate = useNavigate();
  const route = useLocation();

  const roleName = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const isSuperAdmin = roleName === 'SUPERADMIN';

  const searchParamValue = new URLSearchParams(route.search).get('tab');

  useEffect(() => {
    if (searchParamValue === 'role') {
      setActiveTab('ROLES');
    } else if (searchParamValue === 'menu' && isSuperAdmin) {
      setActiveTab('DASHBOARD_MENU');
    }
  }, [searchParamValue, isSuperAdmin]);

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <h1>Admin Settings Overview</h1>
      </div>
      <div className={styles['details-toggler']}>
        <span
          className={activeTab === 'ADMINS' ? styles['active-toggle'] : ''}
          onClick={() => setActiveTab('ADMINS')}>
          List of Admins
        </span>

        <span
          className={activeTab === 'ROLES' ? styles['active-toggle'] : ''}
          onClick={() => setActiveTab('ROLES')}>
          Roles
        </span>

        {isSuperAdmin && (
          <span
            className={
              activeTab === 'DASHBOARD_MENU' ? styles['active-toggle'] : ''
            }
            onClick={() => setActiveTab('DASHBOARD_MENU')}>
            Dashboard Menu
          </span>
        )}
      </div>
      <div className={styles.content}>
        {activeTab !== 'DASHBOARD_MENU' && (
          <div className={styles.topButton}>
            <Button
              onClick={() => navigate('/dashboard/create-role')}
              className={styles.button}
              label="Create New Role"
            />
          </div>
        )}
        {activeTab === 'ADMINS' && <AdminUsers />}

        {activeTab === 'ROLES' && <AdminRoles />}

        {activeTab === 'DASHBOARD_MENU' && isSuperAdmin && (
          <AdminDashboardMenuSettings />
        )}
      </div>
    </div>
  );
};

export const AdminUsers = () => {
  const [modalId, setModalId] = useState({id: '', type: '', open: false});
  const [form] = Form.useForm();

  const {data: roles, loading: loadingRoles} = useQuery(GET_ALL_USER_ROLES, {
    variables: {
      filter: {
        limit: 50,
      },
    },
  });
  const [assignUserRole, {loading: loadingAssign}] =
    useMutation(ASSIGN_USER_ROLE);
  const [deleteAdminUser, {loading: loadingDelete}] =
    useMutation(DELETE_ADMIN_USER);

  const {data, loading, refetch} = useQuery(GET_ALL_ADMIN_USERS, {
    variables: {
      filters: {
        user: {
          admin: true,
        },
      },
    },
  });

  const handleUpdateRole = values => {
    assignUserRole({
      variables: {
        userId: modalId.id,
        roleIds: values.role,
      },
    })
      .then(({data: assignUserRole}) => {
        const {statusCode, __typename, message} =
          assignUserRole?.assignUserRole;
        if (__typename === 'Error' || statusCode === 400) {
          toast.error(`Something went wrong!!! - ${message} `);
        } else {
          toast.success('Reassigned Successfully');
          setModalId({open: false});
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err.message}`));
  };

  const handleDeleteAdmin = () => {
    deleteAdminUser({
      variables: {
        userId: modalId.id,
      },
    })
      .then(({data: deleteAdminUser}) => {
        if (
          deleteAdminUser.__typename === 'Error' ||
          deleteAdminUser.statusCode === 400
        ) {
          toast.error(`Something went wrong!!! - ${deleteAdminUser.message} `);
        } else {
          toast.success('Admin User Deleted Successfully');
          setModalId({open: false});
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err.message}`));
  };

  const columns = [
    {
      dataIndex: 'email',
      key: 'email',
      title: 'Email',
      render: (text, render) => <span>{render?.email}</span>,
    },
    {
      dataIndex: 'Role',
      key: 'role',
      title: 'Role',
      render: (text, render) => {
        const roleName = render?.activeAdminTab?.name?.split('_')?.join(' ');
        const roleParts = roleName
          ?.split(',')
          .map(part => part.trim())
          .filter(part => part);

        return (
          <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
            {roleParts?.map((part, index) => (
              <Tag key={index} color="blue">
                <span>{part}</span>
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, render) => (
        <Dropdown
          trigger="hover"
          overlay={
            <Menu>
              {render?.activeAdminTab?.name !== 'SUPERADMIN' ? (
                <>
                  <Menu.Item
                    onClick={() => {
                      setModalId({
                        id: render?.id,
                        open: true,
                        type: 'REASSIGN',
                      });
                      form.setFieldsValue({
                        role: render?.activeAdminTab?.id,
                      });
                    }}
                    key="1">
                    <div>
                      <span>Reassign Role</span>
                    </div>
                  </Menu.Item>
                  <Menu.Item
                    onClick={() =>
                      setModalId({id: render?.id, open: true, type: 'DELETE'})
                    }
                    key="2">
                    <div>
                      <span>Delete Admin</span>
                    </div>
                  </Menu.Item>
                </>
              ) : (
                <Menu.Item key="2">No Action</Menu.Item>
              )}
            </Menu>
          }>
          <img src={dotsIcon} onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Table
        loading={loading}
        dataSource={data?.getAllUsers?.users}
        columns={columns}
      />
      <DBModal
        isOpen={modalId.open}
        handleClose={() => setModalId({open: false})}
        content={
          <div className={styles.modalContainer}>
            {modalId.type === 'DELETE' ? (
              <div className={styles.deleteModal}>
                <div className={styles.modalIcon}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                      fill="#f56565"
                    />
                  </svg>
                </div>
                <h1 className={styles.modalTitle}>Delete Admin User</h1>
                <p className={styles.modalDescription}>
                  Are you certain you wish to delete this Admin User? Please
                  note that this action <strong>cannot be reversed</strong>.
                </p>
                <div className={styles.modalActions}>
                  <Button
                    disabled={loadingDelete}
                    label="Delete Admin"
                    className={styles.deleteButton}
                    onClick={handleDeleteAdmin}
                  />
                  <Button
                    label="Cancel"
                    type="secondary"
                    className={styles.cancelButton}
                    onClick={() => setModalId({open: false})}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.updateModal}>
                <div className={styles.modalIcon}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                      fill="#8d6cfa"
                    />
                  </svg>
                </div>
                <h1 className={styles.modalTitle}>Update Admin Role</h1>
                <p className={styles.modalDescription}>
                  Select the new role(s) for this admin user. You can assign
                  multiple roles if needed.
                </p>
                <Form
                  onFinish={handleUpdateRole}
                  layout="vertical"
                  form={form}
                  className={styles.updateForm}>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Please select at least one role',
                      },
                    ]}
                    name="role"
                    className={styles.formField}
                    label="Admin Roles">
                    <Select
                      filterOption
                      optionFilterProp="filter"
                      mode="multiple"
                      loading={loadingRoles}
                      placeholder="Select role(s)"
                      className={styles.roleSelect}>
                      {roles?.getAllUserRole?.data
                        ?.filter(
                          data => !['SUPERADMIN', 'USER'].includes(data?.name),
                        )
                        ?.map(data => (
                          <Select.Option
                            key={data.id}
                            filter={data.description}>
                            {data.description}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                  <div className={styles.modalActions}>
                    <SubmitButton
                      disabled={loadingAssign}
                      label="Update Role"
                      className={styles.updateButton}
                    />
                    <Button
                      label="Cancel"
                      type="secondary"
                      className={styles.cancelButton}
                      onClick={() => setModalId({open: false})}
                    />
                  </div>
                </Form>
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

export const AdminRoles = () => {
  const {data, loading} = useQuery(GET_ALL_USER_ROLES, {
    variables: {
      filter: {
        limit: 50,
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const navigate = useNavigate();
  const column = [
    {
      dataIndex: 'name',
      key: 'Name',
      title: 'Name',
      render: (text, render) => (
        <Tag color="blue">{render?.name?.split('_')?.join(' ')}</Tag>
      ),
    },
    {
      dataIndex: 'permissions',
      key: 'permissions',
      title: 'Permissions',
      render: (text, record) => <span>{record?.tabs.length}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, render) => (
        <Dropdown
          trigger="hover"
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => navigate(`/dashboard/update-role/${render?.id}`)}
                key="1">
                <div>
                  <span>Update Permissions</span>
                </div>
              </Menu.Item>
            </Menu>
          }>
          <img src={dotsIcon} onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];
  return (
    <Table
      columns={column}
      loading={loading}
      dataSource={data?.getAllUserRole?.data.filter(
        data => !['SUPERADMIN', 'USER']?.includes(data?.name),
      )}
    />
  );
};

export default AdminSettingOverview;
